import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ListOrdered, ChevronRight, Brain } from 'lucide-react';

/**
 * @param {object} props
 * @param {object} props.insights - learning insights payload
 * @param {object[]} props.reviewSessions
 */
export default function RevisitQueuePanel({ insights, reviewSessions }) {
  const weak = insights?.weak_topics || [];
  const patterns = insights?.confusion_patterns || [];
  const inProgress = (reviewSessions || []).filter((s) => s.status === 'in_progress');

  const weakSet = new Set(weak.map((w) => (w.topic || '').toLowerCase()));
  const topicRows = [];
  for (const w of weak.slice(0, 6)) {
    topicRows.push({
      key: `weak-${w.topic}`,
      title: w.topic,
      meta: `${Math.round(w.correct_rate)}% correct · ${w.questions_total} Qs`,
      href: '/review',
    });
  }
  for (const p of patterns) {
    if (topicRows.length >= 8) break;
    const t = (p.topic || '').toLowerCase();
    if (t && weakSet.has(t)) continue;
    topicRows.push({
      key: `pattern-${p.topic}`,
      title: p.topic,
      meta: `${p.confusion_count} repeated confusions`,
      href: '/qa',
    });
  }

  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ListOrdered className="w-4 h-4 text-muted-foreground" aria-hidden />
          <CardTitle className="font-serif text-lg font-semibold">Revisit queue</CardTitle>
        </div>
        <CardDescription>
          Topics worth another pass, plus reviews you started and have not finished.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {inProgress.length > 0 && (
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
              In progress
            </p>
            <ul className="space-y-1">
              {inProgress.slice(0, 4).map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/review/${s.id}`}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/50 transition-colors active:scale-[0.99]"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Brain className="w-3.5 h-3.5 text-primary flex-shrink-0" aria-hidden />
                      <span className="truncate">{s.title || 'Review session'}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
            Topics
          </p>
          {topicRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center rounded-lg border border-dashed border-border/70 bg-muted/20">
              No weak spots detected yet. Run a review to populate this list.
            </p>
          ) : (
            <ul className="space-y-1">
              {topicRows.map((row) => (
                <li key={row.key}>
                  <Link
                    to={row.href}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/50 transition-colors active:scale-[0.99]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-foreground">{row.title}</span>
                      <span className="block text-xs text-muted-foreground">{row.meta}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button variant="secondary" size="sm" className="w-full" asChild>
          <Link to="/review">Start a review</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
